import { useState, type ReactNode } from "react";
import "./GroupTree.css";

export interface GroupTreeNode {
  key: string;
  label: string;
  count: number;
  totalValue?: string;
  children: ReactNode;
}

export interface GroupTreeProps {
  groups: GroupTreeNode[];
  defaultOpen?: boolean;
  emptyLabel?: string;
}

/** Collapsible group headers (label, count, consolidated total) revealing nested rows on click. */
export function GroupTree({ groups, defaultOpen = false, emptyLabel }: GroupTreeProps) {
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => (defaultOpen ? new Set(groups.map((g) => g.key)) : new Set()));

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (groups.length === 0) {
    return emptyLabel ? <p className="crm-kanban-empty">{emptyLabel}</p> : null;
  }

  return (
    <div className="ui-group-tree" role="list">
      {groups.map((group) => {
        const isOpen = openKeys.has(group.key);
        return (
          <div key={group.key} className={`ui-group-tree__group${isOpen ? " is-open" : ""}`} role="listitem">
            <button
              type="button"
              className="ui-group-tree__header"
              aria-expanded={isOpen}
              onClick={() => toggle(group.key)}
            >
              <span className="ui-group-tree__chevron" aria-hidden="true">▶</span>
              <span className="ui-group-tree__label">{group.label}</span>
              <span className="ui-group-tree__count">({group.count})</span>
              {group.totalValue ? <span className="ui-group-tree__total">{group.totalValue}</span> : null}
            </button>
            {isOpen && <div className="ui-group-tree__body">{group.children}</div>}
          </div>
        );
      })}
    </div>
  );
}
